import { BrowserRouter, Route, Routes } from "react-router-dom";
import { GrainLoader } from "@/components/ui/GrainLoader";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { SidebarProvider } from "./contexts/SidebarContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import InstallPrompt from "./components/InstallPrompt";
import SplashScreen from "./components/SplashScreen";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useUserAuth } from "./hooks/useUserAuth";
import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import * as Sentry from "@sentry/react";
import { usePostHogPageView } from "./hooks/usePostHogTracking";
import { Button } from "./components/ui/button";

import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/firebase";
import { LogoutComponent } from "./components/Logout";
import { useUser } from "./hooks/useUser";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { ComposerGateSync } from "./components/ComposerGateSync";
import { DialogProvider } from "./contexts/DialogContext";
import { StreamingProvider } from "./contexts/StreamingContext";
import { AppUpdateProvider } from "./contexts/AppUpdateContext";
import { DeepModeProvider } from "./contexts/DeepModeContext";
import { ViewAsUserProvider } from "./contexts/ViewAsUserContext";
import {
  DashboardThemeProvider,
  useDashboardTheme,
} from "./components/dashboard/DashboardThemeProvider";
import RbacFallback from "./components/rbac/RbacFallback";
import { useServiceStatus } from "./hooks/useServiceStatus";
import { IS_LOCAL_DEV, usesLocalIqBackend } from "./config/localDev";
import { useQueryClient } from "@tanstack/react-query";
import { myAccessTreeKey } from "./hooks/useMyAccessTree";
import { fetchMyAccessTree } from "./services/apis/useCaseApi";
import { BOOT_MARKS, markBoot } from "./helpers/bootMarks";
import { ContextConsoleProviders } from "./contexts/ContextConsoleProviders";
import { TabStatusManager } from "./components/TabStatusManager";
import { ROUTES } from "./constants";

// Use lazyWithRetry for code splitting with chunk-error recovery
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const SlackConnectStatus = lazyWithRetry(() => import("./pages/SlackConnectStatus"));
// The ONE app shell. Every non-auth route resolves into it; the former standalone
// section shells (Admin / Settings / Dashboards) are now content-only inner routes
// of this shell (see Threads.tsx), so they are lazy-loaded there, not here.
const Threads = lazyWithRetry(() => import("./pages/Threads"));

/**
 * Fallback UI for Sentry.ErrorBoundary wrapping Threads; shows a reload prompt on runtime errors.
 * @returns A centered card with a reload button for error recovery.
 */
function ThreadsErrorFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="text-center space-y-4 max-w-md">
        <p className="text-lg font-medium text-text-medium-emphasis">
          Something went wrong loading this page.
        </p>
        <p className="text-sm text-muted-foreground">
          Try reloading the page to continue.
        </p>
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="w-full"
        >
          Reload page
        </Button>
      </div>
    </div>
  );
}

// Main app wrapper that includes the splash screen
function App() {
  const { sharedCookieQuery } = useUserAuth();
  const { getUserDetails } = useUser();
  //@ts-ignore
  const [companyId, setCompanyId] = useState<number>();
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
  }, [sharedCookieQuery.data]);

  // Boot instrumentation (P4): marks only, no measures, no telemetry. These
  // bracket the ~1.1s window between the Firebase lookup finishing and the API
  // burst starting, so a lab run can attribute it instead of guessing.
  useEffect(() => {
    markBoot(BOOT_MARKS.APP_MOUNT);
  }, []);

  useEffect(()=>{
    const userDetails = getUserDetails();
    if (userDetails) markBoot(BOOT_MARKS.USER_DETAILS_RESOLVED);
    setCompanyId(userDetails?.company?.companyId);
  }, [])

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, () => {
      markBoot(BOOT_MARKS.AUTH_INITIALIZED);
      setAuthInitialized(true);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  return (
    // reducedMotion="user" makes every descendant framer-motion component honor the
    // OS `prefers-reduced-motion: reduce` setting automatically: transform/layout
    // animations are dropped while opacity fades are preserved (fallback, never removal).
    <MotionConfig reducedMotion="user">
    <div className="purple-theme">
      {/*
        Boot LCP P2: this wrapper used to sit at `opacity-0` until the splash
        finished, so NOTHING contentful painted until then — measured FCP 5992ms
        on dev, with the boot skeleton rendered but invisible the whole time.
        The splash is already an opaque `fixed inset-0 z-50` layer, so it covers
        this content on its own; dropping the gate lets the shell paint behind it
        and the splash fade out to reveal an already-painted screen.
        `z-40` keeps content below the splash (z-50) while it is up, and the
        splash unmounts itself afterwards so nothing swallows pointer events.
      */}
      <div className="absolute inset-0 z-40">
        <AuthProvider>
          <DialogProvider>
            <SubscriptionProvider>
              <OnboardingProvider>
              <ComposerGateSync />
              <StreamingProvider>
                <AppUpdateProvider>
                  <DeepModeProvider>
                    {/* Design-language preset scope, hoisted app-wide: the toggle
                        now lives in the nav footer and its `--dash-*` tokens resolve
                        everywhere via `data-dash-theme` — stamped on `#root` by the
                        provider (covers the sibling toast layer too) and on `div.App`
                        below for a flash-free render-time stamp on app content. */}
                    <DashboardThemeProvider>
                      {/* SarasAdmin "View as user" (doAs). Above the route tree so
                          the selection survives navigation between the flat and
                          use-case dashboard views; in-memory only, so a reload
                          always lands the admin back in their own identity. */}
                      <ViewAsUserProvider>
                        <AppContent />
                      </ViewAsUserProvider>
                    </DashboardThemeProvider>
                  </DeepModeProvider>
                </AppUpdateProvider>
              </StreamingProvider>
              </OnboardingProvider>
            </SubscriptionProvider>
          </DialogProvider>
        </AuthProvider>
      </div>

      {/* Show splash screen while auth is initializing, held just long enough to avoid a flash on fast auth */}
      <SplashScreen
        isLoading={!authInitialized}
        onFinish={() => markBoot(BOOT_MARKS.SPLASH_FINISHED)}
        minDisplayTime={300}
      />
    </div>
    </MotionConfig>
  );
}

function PostHogPageViewTracker() {
  usePostHogPageView();
  return null;
}

// Inner component that will only render after AuthProvider is ready.
// Exported for tests — App itself pulls in the whole provider stack.
export function AppContent() {
  const { currentUser, userDetails, roleAccessError } = useAuth();
  const queryClient = useQueryClient();
  // Read the app-level design-language preset so it can be stamped on the root
  // `div.App` — this is what makes every `--dash-*` token (nav, toasts, creator
  // panel, preview) resolve app-wide. `.dark` lives on <html>, a descendant
  // relationship that keeps `.dark [data-dash-theme=…]` selectors valid.
  const { dashTheme } = useDashboardTheme();

  // Support-banner status isn't on the local iq-backend; skip when running locally
  // (`pnpm local`) or hybrid E2E (`dev:test` + VITE_IQ_API_URL → 127.0.0.1:8000).
  const shouldCheckServiceStatus =
    Boolean(currentUser) && !IS_LOCAL_DEV && !usesLocalIqBackend();

  // Fail-open: the status check runs in parallel and never blocks the first paint.
  // The disruption screen still swaps in when the answer arrives and is bad.
  const { hasServiceDisruption, nonOperationalServices } = useServiceStatus({
    enabled: shouldCheckServiceStatus,
  });

  const downServiceNames = nonOperationalServices.map((service) => service.name);

  // Warm the nav's use-case access tree as soon as a session exists, instead of
  // waiting for the shell (AppRail) to mount behind the route gates. Same key and
  // queryFn as `useMyAccessTree`, so the rail reads this cache entry rather than
  // firing its own request.
  useEffect(() => {
    if (!userDetails?.userId) return;
    queryClient.prefetchQuery({
      queryKey: myAccessTreeKey(userDetails.userId),
      queryFn: fetchMyAccessTree,
      staleTime: 5 * 60 * 1000,
    });
  }, [userDetails?.userId, queryClient]);

  return (
    <>
      <BrowserRouter>
        <PostHogPageViewTracker />
        <TabStatusManager />
        <ContextConsoleProviders>
      {
        hasServiceDisruption ? (
        <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
          <RbacFallback
            type="serviceDown"
            downServices={downServiceNames}
          />
        </div>
        ) : roleAccessError ? 
        <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
          <RbacFallback error={roleAccessError} showAvatar={true} />
        </div> : 
        <SidebarProvider>
          <div className="App bg-tint-secondary" data-dash-theme={dashTheme}>
            <ChunkErrorBoundary>
            <Routes>
              <Route
                path="/auth"
                element={
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
                        <GrainLoader />
                      </div>
                    }
                  >
                    <Auth />
                  </Suspense>
                }
              />
              <Route
                path="/logout"
                element={
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
                        <GrainLoader />
                      </div>
                    }
                  >
                    <LogoutComponent />
                  </Suspense>
                }
              ></Route>
              <Route
                path={ROUTES.SLACK_CONNECT_STATUS}
                element={
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
                        <GrainLoader />
                      </div>
                    }
                  >
                    <SlackConnectStatus />
                  </Suspense>
                }
              />
              {/* Option A — one persistent shell. Every non-auth route (chat,
                  /requests, /reports, /dashboards/*, /settings/*, /admin/*)
                  resolves INTO this shell as an inner route; the per-section role
                  guards that used to wrap the standalone shells here now live on
                  the inner route elements in Threads.tsx (unchanged bounce
                  behavior). ProtectedRoute still fronts the shell as before. */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    {/* Sentry.ErrorBoundary catches runtime errors in Threads + useChats.
                        Fixes SARAS-IQ-DEV-B2 by providing graceful fallback instead of blank screen. */}
                    <Sentry.ErrorBoundary fallback={<ThreadsErrorFallback />}>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center h-screen bg-[var(--dash-paper,var(--structural-white))]">
                            <GrainLoader />
                          </div>
                        }
                      >
                        <Threads />
                      </Suspense>
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
            </Routes>
            </ChunkErrorBoundary>
          </div>
        </SidebarProvider>
      }
        </ContextConsoleProviders>
      </BrowserRouter>

      <Toaster />
      <InstallPrompt />
    </>
  );
}

export default App;